import React, { useState, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Spinner, Chip } from '@heroui/react';
import { SearchIcon, Plus, X, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCourseEnrollments, useEnrollUser, useUnenrollUser } from '@/hooks/queries/useCourses';
import { useInfiniteUsers } from '@/hooks/queries/useUsers';
import { User } from '@/services/apis/users';
import { useDebounce } from '@/hooks/useDebounce';

const enrollUserSchema = z.object({
  deadline: z.string().optional(),
});

type EnrollUserFormData = z.infer<typeof enrollUserSchema>;

interface AssignUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId?: string | null;
}

const AssignUsersModal: React.FC<AssignUsersModalProps> = ({ isOpen, onClose, courseId }) => {
  const { t } = useTranslation('admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [userToEnroll, setUserToEnroll] = useState<User | null>(null);
  const debouncedSearch = useDebounce(searchQuery, 500);

  const { control, handleSubmit, reset } = useForm<EnrollUserFormData>({
    resolver: zodResolver(enrollUserSchema),
    defaultValues: {
      deadline: '',
    },
  });

  const { data: enrollments, isLoading: isLoadingEnrollments } = useCourseEnrollments(courseId || '');
  const enrollUserMutation = useEnrollUser();
  const unenrollUserMutation = useUnenrollUser();
  const {
    data: usersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingUsers,
  } = useInfiniteUsers({
    limit: 20,
    search: debouncedSearch || undefined,
  });

  const users = useMemo(() => {
    return (
      usersData?.pages.flatMap((page) => {
        return page?.data || [];
      }) || []
    );
  }, [usersData]);

  const enrolledUserIds = useMemo(() => {
    return new Set(enrollments?.map((e) => e.user.id) || []);
  }, [enrollments]);

  const availableUsers = useMemo(() => {
    return users.filter((user) => !enrolledUserIds.has(user.id));
  }, [users, enrolledUserIds]);

  const handleSelectUserForEnroll = (user: User) => {
    setUserToEnroll(user);
    reset({ deadline: '' });
  };

  const handleCancelEnroll = () => {
    setUserToEnroll(null);
    reset({ deadline: '' });
  };

  const onSubmitEnroll = async (data: EnrollUserFormData) => {
    if (!courseId || !userToEnroll) return;

    await enrollUserMutation.mutateAsync({
      courseId,
      userId: userToEnroll.id,
      data: data.deadline ? { deadline: new Date(data.deadline).toISOString() } : undefined,
    });
    setUserToEnroll(null);
    reset({ deadline: '' });
  };

  const handleUnenrollUser = async (userId: string) => {
    if (!courseId) return;
    await unenrollUserMutation.mutateAsync({
      courseId,
      userId,
    });
  };

  const getUserName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.email;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" placement="center" backdrop="blur" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">{t('courses.assignUsers.title')}</ModalHeader>
        <ModalBody>
          <div className="space-y-6">
            {/* Search for users to add */}
            <div className="space-y-4">
              <h3 className="font-semibold">{t('courses.assignUsers.addUser')}</h3>
              <Input
                isClearable
                placeholder={t('courses.assignUsers.searchPlaceholder')}
                startContent={<SearchIcon />}
                value={searchQuery}
                onClear={() => setSearchQuery('')}
                onValueChange={setSearchQuery}
              />

              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {isLoadingUsers && availableUsers.length === 0 ? (
                  <div className="flex justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">{t('courses.assignUsers.noUsersFound')}</div>
                ) : (
                  availableUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex flex-col p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      {userToEnroll?.id === user.id ? (
                        // Show deadline form when user is selected
                        <form onSubmit={handleSubmit(onSubmitEnroll)} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{getUserName(user)}</p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex items-end gap-2">
                            <Controller
                              name="deadline"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  type="date"
                                  label={t('courses.assignUsers.deadline')}
                                  placeholder={t('courses.assignUsers.deadlinePlaceholder')}
                                  size="sm"
                                  className="flex-1"
                                  min={new Date().toISOString().split('T')[0]}
                                  description={t('courses.assignUsers.deadlineDescription')}
                                />
                              )}
                            />
                            <Button
                              size="sm"
                              color="default"
                              variant="flat"
                              type="button"
                              onPress={handleCancelEnroll}
                            >
                              {t('common.cancel')}
                            </Button>
                            <Button
                              size="sm"
                              color="primary"
                              type="submit"
                              isLoading={enrollUserMutation.isPending}
                            >
                              {t('courses.assignUsers.add')}
                            </Button>
                          </div>
                        </form>
                      ) : (
                        // Show default user row
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{getUserName(user)}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <Button
                            size="sm"
                            color="primary"
                            variant="flat"
                            startContent={<Plus className="w-4 h-4" />}
                            onPress={() => handleSelectUserForEnroll(user)}
                          >
                            {t('courses.assignUsers.add')}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}

                {hasNextPage && (
                  <div className="flex justify-center py-2">
                    <Button variant="flat" onPress={() => fetchNextPage()} isLoading={isFetchingNextPage} size="sm">
                      {t('common.loadMore')}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Currently enrolled users */}
            <div className="space-y-4">
              <h3 className="font-semibold">{t('courses.assignUsers.enrolledUsers')}</h3>
              {isLoadingEnrollments ? (
                <div className="flex justify-center py-4">
                  <Spinner size="sm" />
                </div>
              ) : enrollments && enrollments.length > 0 ? (
                <div className="space-y-2">
                  {enrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      className="flex items-center justify-between p-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {enrollment.user.firstName && enrollment.user.lastName
                              ? `${enrollment.user.firstName} ${enrollment.user.lastName}`
                              : enrollment.user.email}
                          </p>
                          {enrollment.deadline && (
                            <Chip
                              size="sm"
                              variant="flat"
                              color="warning"
                              startContent={<Calendar className="w-3 h-3" />}
                            >
                              {new Date(enrollment.deadline).toLocaleDateString()}
                            </Chip>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{enrollment.user.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t('courses.assignUsers.enrolledBy')}: {enrollment.enrolledBy.email} •{' '}
                          {new Date(enrollment.enrolledAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        color="danger"
                        variant="light"
                        isIconOnly
                        onPress={() => handleUnenrollUser(enrollment.user.id)}
                        isLoading={unenrollUserMutation.isPending}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">{t('courses.assignUsers.noEnrollments')}</div>
              )}
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AssignUsersModal;
